import type { IAnnouncementRepository, PaginatedResponse, QueryParams } from '@yawi/domain';
import { buildQueryString, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';
import type { Announcement } from '@yawi/types';
import { AnnouncementType } from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

// Tipos de respuesta de la API
interface ApiAnnouncementResponse {
  id: string;
  title: string;
  body: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  imageUrl?: string | null;
  publishedAt?: string | null;
  expiresAt?: string | null;
  isPublished: boolean;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  sectionIds?: string[];
}

interface ApiCreateAnnouncementRequest {
  title: string;
  body: string;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  imageUrl?: string;
  publishedAt?: string;
  expiresAt?: string;
  isPublished?: boolean;
  isActive?: boolean;
  sectionIds?: string[];
}

interface ApiUpdateAnnouncementRequest {
  title?: string;
  body?: string;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  imageUrl?: string;
  publishedAt?: string;
  expiresAt?: string;
  isPublished?: boolean;
  isActive?: boolean;
  sectionIds?: string[];
}

// Mapea la respuesta de la API al tipo Announcement interno
const mapApiAnnouncementToAnnouncement = (apiAnnouncement: ApiAnnouncementResponse): Announcement => {
  // Mapear priority a type
  let type: AnnouncementType = AnnouncementType.INFO;
  if (apiAnnouncement.priority === 'URGENT') type = AnnouncementType.URGENT;
  else if (apiAnnouncement.priority === 'HIGH') type = AnnouncementType.IMPORTANT;
  else if (apiAnnouncement.priority === 'MEDIUM') type = AnnouncementType.INFO;
  else type = AnnouncementType.INFO;

  return {
    id: apiAnnouncement.id,
    title: apiAnnouncement.title,
    content: apiAnnouncement.body,
    type,
    date: apiAnnouncement.publishedAt || apiAnnouncement.createdAt,
    expiresAt: apiAnnouncement.expiresAt || undefined,
    attachmentUrl: apiAnnouncement.imageUrl || undefined,
    sectionIds: apiAnnouncement.sectionIds ?? [],
    poll: undefined, // Los polls se manejan por separado en la API
    isPublished: apiAnnouncement.isPublished,
  };
};

export class ApiAnnouncementRepository implements IAnnouncementRepository {
  async getAllAnnouncements(): Promise<Announcement[]> {
    const client = getApiClient();
    const response = await client.get<ApiAnnouncementResponse[]>('v1/announcements/active');
    return response.map(mapApiAnnouncementToAnnouncement);
  }

  async getAnnouncements(params?: QueryParams): Promise<PaginatedResponse<Announcement>> {
    const client = getApiClient();
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      search: params?.search,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      ...params,
    });
    const response = await client.get<PaginatedResponse<ApiAnnouncementResponse>>(
      `v1/announcements?${queryString}`
    );
    return {
      ...response,
      data: response.data.map(mapApiAnnouncementToAnnouncement),
    };
  }

  async createAnnouncement(data: Omit<Announcement, 'id' | 'date'>): Promise<Announcement> {
    const client = getApiClient();
    
    // Mapear type a priority
    let priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT' = 'MEDIUM';
    if (data.type === AnnouncementType.URGENT) priority = 'URGENT';
    else if (data.type === AnnouncementType.IMPORTANT) priority = 'HIGH';
    else if (data.type === AnnouncementType.INFO) priority = 'MEDIUM';
    else priority = 'LOW';

    const request: ApiCreateAnnouncementRequest = {
      title: data.title,
      body: data.content,
      priority,
      imageUrl: data.attachmentUrl,
      expiresAt: data.expiresAt,
      isPublished: true,
      isActive: true,
      sectionIds: data.sectionIds,
    };
    const apiAnnouncement = await client.post<ApiAnnouncementResponse>('v1/announcements', request);
    return mapApiAnnouncementToAnnouncement(apiAnnouncement);
  }

  async deleteAnnouncement(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/announcements/${id}`);
  }

  async voteInPoll(announcementId: string, optionId: string, userId: string): Promise<void> {
    const client = getApiClient();
    // La API usa polls separados, no announcements con polls
    await client.post(`v1/polls/${announcementId}/vote`, {
      optionIds: [optionId],
    });
  }

  async getAnnouncementById(id: string): Promise<Announcement | null> {
    const client = getApiClient();
    try {
      const apiAnnouncement = await client.get<ApiAnnouncementResponse>(`v1/announcements/${id}`);
      return mapApiAnnouncementToAnnouncement(apiAnnouncement);
    } catch {
      return null;
    }
  }

  async updateAnnouncement(id: string, data: Partial<Omit<Announcement, 'id' | 'date'>>): Promise<Announcement> {
    const client = getApiClient();

    // Mapear type a priority si se proporciona
    let priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT' | undefined;
    if (data.type) {
      if (data.type === AnnouncementType.URGENT) priority = 'URGENT';
      else if (data.type === AnnouncementType.IMPORTANT) priority = 'HIGH';
      else if (data.type === AnnouncementType.INFO) priority = 'MEDIUM';
      else priority = 'LOW';
    }

    const request: ApiUpdateAnnouncementRequest = {
      title: data.title,
      body: data.content,
      priority,
      imageUrl: data.attachmentUrl,
      expiresAt: data.expiresAt,
      isPublished: data.isPublished,
      sectionIds: data.sectionIds,
    };

    // Remove undefined values
    Object.keys(request).forEach(key => {
      if (request[key as keyof ApiUpdateAnnouncementRequest] === undefined) {
        delete request[key as keyof ApiUpdateAnnouncementRequest];
      }
    });

    const apiAnnouncement = await client.put<ApiAnnouncementResponse>(`v1/announcements/${id}`, request);
    return mapApiAnnouncementToAnnouncement(apiAnnouncement);
  }

  async publishAnnouncement(id: string): Promise<Announcement> {
    const client = getApiClient();
    const apiAnnouncement = await client.patch<ApiAnnouncementResponse>(`v1/announcements/${id}/publish`);
    return mapApiAnnouncementToAnnouncement(apiAnnouncement);
  }

  async unpublishAnnouncement(id: string): Promise<Announcement> {
    const client = getApiClient();
    const apiAnnouncement = await client.patch<ApiAnnouncementResponse>(`v1/announcements/${id}/unpublish`);
    return mapApiAnnouncementToAnnouncement(apiAnnouncement);
  }
}

